import React from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button } from "react-bootstrap";

/* =========================
   Helpers
========================= */
const sanitizeSheetName = (name) => {
  if (!name) return "SN";
  return name.substring(0, 31).replace(/[:\\/*?[\]]/g, "_");
};

const sanitizeFileName = (name) =>
  name.replace(/[^a-zA-Z0-9_-]/g, "");

const truncate = (str, max) =>
  str.length > max ? str.substring(0, max) : str;

const getTodayString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
};


/* =========================
   Component
========================= */
const ExportComponentComplete = ({ summary, alarmsDF, dateRange }) => {
  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();

    // -----------------------------
    // 1) COVER + SUMMARY
    // -----------------------------
    const sheet = workbook.addWorksheet("Summary");

    sheet.addRow(["ANALISI STATISTICA ERRORI MACCHINA"]);
    sheet.addRow([]);
    sheet.addRow(["Periodo Analizzato:", `${dateRange[0]} - ${dateRange[1]}`]);
    sheet.addRow([]);
    sheet.addRow(["Numero Macchine Analizzate:", summary.length]);
    sheet.addRow([]);

    sheet.mergeCells("A1:F1");
    const titleCell = sheet.getCell("A1");
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    titleCell.font = { bold: true, size: 18 };

    const coverRowsCount = sheet.rowCount;

    const summaryHeader = [
      "#",
      "Cliente",
      "SN Machine",
      "Errore",
      "Numero Occorrenze",
      "Running Time (ore)"
    ];

    const headerRow = sheet.addRow(summaryHeader);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "ffffae1a" }
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    summary.forEach((row, i) => {
      sheet.addRow([
        i + 1,
        row.customerName,
        row.snMachine,
        row.alarmNo,
        row.nError,
        row.RunningTime.toFixed(2)
      ]);
    });

    sheet.eachRow({ includeEmpty: true }, (row) => {
      row.alignment = { vertical: "middle", horizontal: "center" };
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
      });
    });

    sheet.getRow(coverRowsCount + 1).font = { bold: true };

    sheet.columns.forEach((col) => {
      let maxLength = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const length = cell.value ? cell.value.toString().length : 0;
        if (length > maxLength) maxLength = length;
      });
      col.width = maxLength + 5;
    });

    // -----------------------------
    // 2) FOGLI PER SN MACHINE
    // -----------------------------
    if (alarmsDF) {
      let alarmsData = {};
      try {
        alarmsData =
          typeof alarmsDF === "string" ? JSON.parse(alarmsDF) : alarmsDF;
      } catch (err) {
        console.error("Errore parsing alarmsDF:", err);
      }

      const snMachines = [
        ...new Set(Object.values(alarmsData.mainMachineSerialNo || {}))
      ];

      snMachines.forEach((sn) => {
        const ws = workbook.addWorksheet(sanitizeSheetName(sn));

        const header = [
          "Customer",
          "SerialNumber",
          "Device Name",
          "Alarm No",
          "Alarm text",
          "Time",
          "Tool",
          "Material",
          "NC Program",
          "Previous State",
          "Next State",
          "From Restart"
        ];

        const summaryRow = ws.addRow(header);
        summaryRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "ffffae1a" }
          };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        Object.keys(alarmsData.mainMachineSerialNo || {}).forEach((key) => {
          if (alarmsData.mainMachineSerialNo[key] === sn) {
            ws.addRow([
              alarmsData.customerName[key] || "",
              sn,
              alarmsData.deviceName[key] || "",
              alarmsData.alarmNo[key] || "",
              alarmsData.text[key] || "",
              alarmsData.time[key] || "",
              alarmsData.tool[key] || "",
              alarmsData.materialName[key] || "",
              alarmsData.nCProgramName[key] || "",
              alarmsData.Previous_state[key] || "",
              alarmsData.Next_state[key] || "",
              alarmsData.Restart[key] || ""
            ]);
          }
        });

        ws.eachRow({ includeEmpty: true }, (row) => {
          row.alignment = { horizontal: "center", vertical: "middle" };
          row.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" }
            };
          });
        });

        ws.columns.forEach((col) => {
          let maxLength = 10;
          col.eachCell({ includeEmpty: true }, (cell) => {
            const length = cell.value ? cell.value.toString().length : 0;
            if (length > maxLength) maxLength = length;
          });
          col.width = maxLength + 5;
        });
      });
    }

    // -----------------------------
    // 3) SALVA FILE (UPDATED)
    // -----------------------------
    const customers = [
      ...new Set(summary.map(r => r.customerName).filter(Boolean))
    ];

    let fileName = `analisi_errori_statistica_${getTodayString()}.xlsx`;

    if (customers.length === 1 && summary.length > 0) {
      const customerName = truncate(
        sanitizeFileName(customers[0]),
        10
      );

      // SN unici
      const snList = [
        ...new Set(summary.map(r => r.snMachine).filter(Boolean))
      ]
        .map(sn => sanitizeFileName(sn))
        .join("-");

      // Alarm unici
      const alarmList = [
        ...new Set(summary.map(r => r.alarmNo).filter(Boolean))
      ]
        .map(a => sanitizeFileName(String(a)))
        .join("-");

      // Limiti di sicurezza
      const safeSnList = truncate(snList, 30);
      const safeAlarmList = truncate(alarmList, 50);

      fileName = `${customerName}_${safeSnList}_${safeAlarmList}.xlsx`;
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);

  };

  return (
    <Button variant="success" onClick={downloadExcel} className="mt-3">
      Esporta Excel
    </Button>
  );
};

export default ExportComponentComplete;
